import type { Zone } from '@/types/zones/zone';
import { getPageConfig } from '@/lib/aggregation';
import { getPlaytechGameConfig, findPlaytechGameInZones, PLAYTECH_FALLBACK_GAME } from './find-playtech-game';
import type { PlaytechGameConfig } from './find-playtech-game';
import { ddRumError } from '@/utils/datadog';
import { ddErrorPokerGameConfigFallback } from '@/utils/datadog/poker';

/**
 * Fetches the lobby (home page) config and parses its zones to find the first
 * Playtech game. Always uses "/" as the source route because Playtech games
 * live in the lobby zones regardless of which page the caller is on — e.g. the
 * rewards page has no Playtech game data in its own zones.
 * Returns PLAYTECH_FALLBACK_GAME when no Playtech game is found.
 */
export const fetchPlaytechGameConfig = async (isMobile: boolean): Promise<PlaytechGameConfig> => {
    const json = await getPageConfig('/');
    const zones = json?.pageConfig?.content?.data?.zones;

    if (!zones) {
        ddRumError(ddErrorPokerGameConfigFallback({ reason: 'no_zones' }));
        return PLAYTECH_FALLBACK_GAME;
    }

    const standardZones = zones.filter((z): z is Zone => Array.isArray((z as Zone)?.data?.body));

    const game = findPlaytechGameInZones(standardZones);

    if (game === null) {
        ddRumError(ddErrorPokerGameConfigFallback({ reason: 'no_playtech_game' }));
        return PLAYTECH_FALLBACK_GAME;
    }

    return getPlaytechGameConfig(game, isMobile);
};
